import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import cn from '../../../utils/cn';
import {
  filterDetailHeaderMenuBySearch,
  getDetailHeaderMenuItemKey,
  getDetailHeaderMenuSwatchColor,
  hasDetailHeaderMenuChildren,
  isDetailHeaderMenuItemClickable,
} from '../../../domain/detailHeader/detailHeaderUseCase';
import SearchField from '../SearchField/SearchField';

import styles from './DetailHeaderMenuList.module.css';

/**
 * @param {ApiDetailHeaderMenuItemTextDecoration | null | undefined} textDecoration
 * @returns {string}
 */
function getLabelDecorationClassName(textDecoration) {
  const td = textDecoration ?? {
    underline: false,
    bold: false,
    italic: false,
    strikeThrough: false,
  };
  return cn([
    styles['label-text'],
    td.bold && styles.bold,
    td.italic && styles.italic,
    td.strikeThrough && styles['strike-through'],
    td.underline && styles.underline,
  ]);
}

/**
 * @param {object} props
 * @param {ApiDetailHeaderMenu | ApiDetailHeaderMenuItem} props.item
 * @param {number} props.depth
 * @param {boolean} props.isClickable
 * @param {(baseUrl: string) => void} [props.onItemClick]
 * @returns {JSX.Element}
 */
function DetailHeaderMenuRow({
  item, depth, isClickable, onItemClick = () => {},
}) {
  const swatchColor = getDetailHeaderMenuSwatchColor(item);
  const indentStyle = { paddingLeft: `calc(${depth} * var(--spacing-s))` };
  const rowClassName = cn([
    styles.row,
    isClickable && styles['row-clickable'],
    isClickable && item.active && styles['row-active'],
  ]);

  const content = (
    <>
      {swatchColor ? (
        <span
          className={styles.bar}
          style={{ backgroundColor: swatchColor }}
          aria-hidden
        />
      ) : null}
      <span className={getLabelDecorationClassName(item.textDecoration)}>
        {item.lib}
      </span>
    </>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        className={rowClassName}
        style={indentStyle}
        onClick={() => {
          if (item.baseUrl) {
            onItemClick(item.baseUrl);
          }
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={rowClassName} style={indentStyle}>
      {content}
    </div>
  );
}

/**
 * @param {object} props
 * @param {ApiDetailHeaderMenu | ApiDetailHeaderMenuItem} props.item
 * @param {number} props.depth
 * @param {(baseUrl: string) => void} props.onItemClick
 * @returns {JSX.Element}
 */
function DetailHeaderMenuNode({
  item, depth, onItemClick,
}) {
  const nodeKey = getDetailHeaderMenuItemKey(item, depth);

  if (hasDetailHeaderMenuChildren(item)) {
    return (
      <div className={styles.node} key={nodeKey}>
        <DetailHeaderMenuRow item={item} depth={depth} isClickable={false} />
        {item.items.map((child) => (
          <DetailHeaderMenuNode
            key={getDetailHeaderMenuItemKey(child, depth + 1)}
            item={child}
            depth={depth + 1}
            onItemClick={onItemClick}
          />
        ))}
      </div>
    );
  }

  const clickable = isDetailHeaderMenuItemClickable(item);

  return (
    <DetailHeaderMenuRow
      key={nodeKey}
      item={item}
      depth={depth}
      isClickable={clickable}
      onItemClick={onItemClick}
    />
  );
}

/**
 * Hierarchical navigation list for the detail header menu popover.
 * @param {object} props
 * @param {ApiDetailHeaderMenu[]} props.menu
 * @param {(baseUrl: string) => void} props.onItemClick
 * @returns {JSX.Element}
 */
function DetailHeaderMenuList({ menu, onItemClick }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filteredMenu = useMemo(
    () => filterDetailHeaderMenuBySearch(menu, search),
    [menu, search],
  );

  const renderList = () => {
    if (!menu?.length) {
      return (
        <p className={styles['empty-message']}>Aucune action disponible</p>
      );
    }

    if (!filteredMenu.length) {
      return (
        <p className={styles['empty-message']}>{t('detail.headerMenu.noResult')}</p>
      );
    }

    return filteredMenu.map((item) => (
      <DetailHeaderMenuNode
        key={getDetailHeaderMenuItemKey(item, 0)}
        item={item}
        depth={0}
        onItemClick={onItemClick}
      />
    ));
  };

  return (
    <div className={styles.container}>
      <div className={styles.search}>
        <SearchField
          id="detail-header-menu-search"
          placeholder={t('detail.headerMenu.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onFocus={() => {}}
        />
      </div>
      <div className={styles.root}>
        {renderList()}
      </div>
    </div>
  );
}

export default DetailHeaderMenuList;
